import React, {
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { store } from "../../Store";
import { FocusScope } from "react-aria";
import { Icon } from "@iconify/react/dist/iconify.js";

import { useTranslations } from "../../../hooks/use-translations";
import useIsMobile from "../../../js/utilities/useIsMobile";
import { useGeneralHeaderDropdown } from "../../../hooks/header/useGeneralHeaderDropdown";
import { useAdvancedSearch } from "../../../hooks/advanced-search/use-advanced-search";
import { useLanguage } from "../../../hooks/use-language";
import { useId } from "../../../hooks/use-id";

import { NavbarBrand } from "../components/NavbarBrand";
import { NavbarNav } from "./components/NavbarNav";
import { SkeletonHeader } from "./components/SkeletonHeader";
import { BookDownloadingPopUp } from "../../book-downloading-popup";
import { HeaderActions } from "../components/header-actions";
import { HeaderLangButton } from "../components/languaje-button/HeaderLangButton";
import { HGDropdownResponsive } from "./components/dropdownResponsiveGeneralHeader/HGDropdownResponsive";


/**
 * Componente del header institucional
 * @returns {JSX.Element} - GeneralHeader
 */
export const GeneralHeader = () => {
  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------

  const lang = useLanguage();

  const textosInterfaz = useTranslations({
    section: "header",
    lang,
    array: true,
  });

  const globalState = useContext(store);
  const { state } = globalState;
  const isMobile = useIsMobile();

  const advancedSearch = useAdvancedSearch();
  const showADropdown = useGeneralHeaderDropdown();

  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------

  const dropdownButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const advancedSearchButtonResRef = useRef(null);
  const [renderizado, setRenderizado] = useState(0);

  const menuId = useId("user-menu");

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [isLoading, setIsLoading] = useState(true);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const isLogged =
    state?.sesionActiva !== null &&
    state?.sesionActiva === "SI";

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  /**
   * Asigna el botón que abre las opciones de búsqueda avanzada.
   */
  useEffect(() => {
    if (renderizado === 0) {
      setRenderizado(renderizado.current + 1);
      return;
    }
    if (advancedSearchButtonResRef.current && isMobile) {
      globalState.dispatch({
        type: "CHANGE",
        path: "advancedSearch.triggerButton",
        value: advancedSearchButtonResRef,
      });
    }
  }, [advancedSearchButtonResRef, renderizado, isMobile]);


  useEffect(() => {
    if (dropdownButtonRef.current) {
      globalState.dispatch({
        type: "CHANGE",
        path: "generalHeaderDropdown.triggerButtonRef",
        value: dropdownButtonRef,
      });
    }
  }, [dropdownButtonRef.current]);

  /**
   * Controla el estado de carga del header.
   */
  useEffect(() => {
    if (state?.datosHeader) {
      setIsLoading(false);
    }
  }, [state?.datosHeader]);

  /**
   * Cierra el dropdown cuando se hace clic fuera
   * del botón o del contenido del menú.
   */
  useEffect(() => {
    if (!isMobile || !showADropdown?.state?.visible) {
      return;
    }

    const handleClickOutside = (event) => {
      const target = event.target;

      const clickedButton =
        dropdownButtonRef.current?.contains(target);

      const clickedDropdown =
        dropdownRef.current?.contains(target);

      if (!clickedButton && !clickedDropdown) {
        showADropdown.setVisible(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [
    isMobile,
    showADropdown?.state?.visible,
  ]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const toggleAdvancedSearch = () => {
    advancedSearch.setVisible(
      !advancedSearch?.state?.visible
    );
  };

  const toggleUserDropdown = () => {
    showADropdown.setVisible(
      !showADropdown?.state?.visible
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div role="status" aria-live="polite">
        <span className="sr-only">
          {textosInterfaz?.general_header_title_load ||
            "Cargando encabezado"}
        </span>

        <SkeletonHeader />
      </div>
    );
  }

  return (
    <div className="general-header">
      <nav
        className="h-100 d-flex"
        aria-label={
          textosInterfaz?.general_header_title ||
          "Navegación principal"
        }
      >
        <NavbarBrand
          textosInterfaz={textosInterfaz}
        />

        {/* Navegación desktop */}
        {!isMobile && (
          <div className="w-100 pr-3">
            <NavbarNav
              textosInterfaz={textosInterfaz}
            />
          </div>
        )}

        {/* Controles mobile */}
        {isMobile && (
          <ul className="header-general-controls ml-auto">
            {/* Búsqueda avanzada */}
            <li className="d-flex align-items-center">
              <button
                ref={advancedSearchButtonResRef}
                type="button"
                className={`btn-libros focusable-red buttonv2 button-primary-var2 p-3 ${
                  advancedSearch?.state?.visible
                    ? "activeButton"
                    : ""
                }`}
                onClick={toggleAdvancedSearch}
                aria-expanded={
                  advancedSearch?.state?.visible
                }
                aria-label={
                  textosInterfaz
                    ?.general_header_buttons_books ||
                  "Abrir búsqueda avanzada de libros"
                }
              >
                {textosInterfaz?.libros || "Libros"}
              </button>
            </li>

            {/* Idioma */}
            <li>
              <HeaderLangButton />
            </li>

            {/* Menú de usuario */}
            <li>
              <button
                ref={dropdownButtonRef}
                type="button"
                onClick={toggleUserDropdown}
                className="focusable-red icon-button icon-responsive"
                aria-expanded={
                  showADropdown?.state?.visible
                }
                aria-controls={menuId}
                aria-label={
                  textosInterfaz
                    ?.general_header_buttons_account ||
                  "Abrir menú de usuario"
                }
              >
                <Icon
                  icon="material-symbols:menu-rounded"
                  width={40}
                />
              </button>
            </li>
          </ul>
        )}
      </nav>

      {/* Dropdown responsive */}
      {isMobile && (
        <div ref={dropdownRef}>
          <HGDropdownResponsive
            textosInterfaz={textosInterfaz}
          />
        </div>
      )}

      {/* Popup de descarga */}
      {state?.descargando && (
        <FocusScope contain>
          <BookDownloadingPopUp />
        </FocusScope>
      )}

      {/* Acciones desktop */}
      {!isMobile && !advancedSearch?.state?.visible && (
        <HeaderActions
          textosInterfaz={textosInterfaz}
          background="dark"
        />
      )}
    </div>
  );
};